import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import CTASection from "../components/CTASection";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", rating: 5, comment: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Customer Reviews | Pool Guru Treasure Coast";
    load();
  }, []);

  const load = async () => {
    const { data } = await api.get("/reviews");
    setReviews(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you! Your review has been submitted for approval.");
      setForm({ name: "", location: "", rating: 5, comment: "", email: "" });
    } catch {
      toast.error("Please fill in all required fields.");
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  return (
    <div data-testid="reviews-page">
      <section className="bg-gradient-to-b from-pink-50/50 to-white py-20">
        <div className="section-container">
          <div className="overline mb-3">Customer Reviews</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 max-w-4xl">
            Real reviews from real <span className="text-brand-pink">Treasure Coast</span> homeowners.
          </h1>
          <div className="flex items-center gap-4 mt-8">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <div>
              <span className="text-2xl font-extrabold text-brand-navy font-heading">{avgRating}</span>
              <span className="text-slate-500 ml-2">from {reviews.length} reviews</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 grid md:grid-cols-2 gap-6">
            {reviews.map((r) => (
              <div
                key={r.id}
                className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg transition-shadow"
                data-testid={`review-${r.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed mb-5 italic">"{r.comment}"</p>
                <div className="pt-5 border-t border-slate-100">
                  <div className="font-bold text-brand-navy">{r.name}</div>
                  <div className="text-sm text-slate-500">{r.location}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start">
            <form onSubmit={submit} className="bg-white border-2 border-brand-pink/20 rounded-2xl p-8 shadow-lg" data-testid="review-form">
              <h2 className="text-2xl font-extrabold mb-2">Share your experience</h2>
              <p className="text-sm text-slate-500 mb-6">Reviews are moderated before publishing.</p>

              <div className="space-y-4">
                <Input
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="review-name"
                />
                <Input
                  placeholder="City / Neighborhood"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="review-location"
                />
                <Input
                  type="email"
                  placeholder="Email (not published)"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="review-email"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-2">Rating</div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm({ ...form, rating: n })}
                        className="focus:outline-none"
                        data-testid={`star-${n}`}
                      >
                        <Star className={`w-8 h-8 ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea
                  placeholder="Tell us about your experience... *"
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  rows={5}
                  required
                  data-testid="review-comment"
                />
                <Button type="submit" disabled={submitting} className="w-full bg-brand-pink hover:bg-brand-pink-hover h-12 text-base" data-testid="review-submit">
                  {submitting ? "Submitting..." : "Submit Review"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
